import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Link as LinkIcon,
  Plus,
  RefreshCw,
  Trash2,
  Calendar,
  Share2,
  Copy,
  RefreshCcw,
  CheckCircle2,
  AlertTriangle,
  ClipboardPaste,
  Download,
  ArrowDownUp,
  Apple,
  Loader2,
  Mail,
  LayoutGrid,
} from "lucide-react";
import { toast } from "sonner";
import OAuthIntegrationCard from "./OAuthIntegrationCard";

function relTime(iso) {
  if (!iso) return "aldri";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 60) return "nå";
  if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} t siden`;
  return `${Math.floor(diff / 86400)} d siden`;
}

export default function CalendarSourcesDialog({ open, onOpenChange, onChanged }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [feed, setFeed] = useState(null);
  const [icloud, setIcloud] = useState(null);
  const [icloudAppleId, setIcloudAppleId] = useState("");
  const [icloudPassword, setIcloudPassword] = useState("");
  const [icloudBusy, setIcloudBusy] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [outlookStatus, setOutlookStatus] = useState(null);
  const { activeBoat } = useAuth();
  const isHousehold = activeBoat?.kind === "household";

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [subsRes, feedRes, icloudRes, googleRes, outlookRes] = await Promise.all([
        api.listCalendarSubscriptions(),
        api.getCalendarFeedInfo().catch(() => ({ data: null })),
        api.getIcloudStatus().catch(() => ({ data: { connected: false } })),
        isHousehold ? api.getGoogleCalStatus().catch(() => ({ data: { configured: false, connected: false } })) : Promise.resolve({ data: null }),
        isHousehold ? api.getOutlookStatus().catch(() => ({ data: { configured: false, connected: false } })) : Promise.resolve({ data: null }),
      ]);
      setSubs(subsRes.data?.subscriptions || []);
      setFeed(feedRes.data);
      setIcloud(icloudRes.data);
      setGoogleStatus(googleRes.data);
      setOutlookStatus(outlookRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [open, isHousehold]);

  useEffect(() => { load(); }, [load]);

  const addSub = async () => {
    const n = name.trim();
    const u = url.trim();
    if (!n || !u) { toast.error("Navn og lenke er påkrevd"); return; }
    setAdding(true);
    try {
      const res = await api.createCalendarSubscription(n, u);
      toast.success(`Lagt til «${n}» · ${res.data?.import?.imported || 0} avtaler importert`);
      setName(""); setUrl("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke legge til");
    } finally { setAdding(false); }
  };

  const syncSub = async (id) => {
    setBusyId(id);
    try {
      const res = await api.syncCalendarSubscription(id);
      toast.success(`Synkronisert · ${res.data?.import?.imported || 0} nye avtaler`);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Synk feilet");
    } finally { setBusyId(null); }
  };

  const deleteSub = async (id, subName) => {
    if (!window.confirm(`Fjerne abonnementet «${subName}»? Allerede importerte avtaler beholdes.`)) return;
    setBusyId(id);
    try {
      await api.deleteCalendarSubscription(id);
      toast.success("Fjernet");
      load();
    } catch { toast.error("Kunne ikke fjerne"); }
    finally { setBusyId(null); }
  };

  const pasteUrl = async () => {
    try { const t = await navigator.clipboard.readText(); if (t) setUrl(t.trim()); }
    catch { toast.error("Utklippstavla er ikke tilgjengelig"); }
  };

  const feedHttpsUrl = feed ? `${window.location.origin}${feed.path}` : "";
  const feedWebcalUrl = feedHttpsUrl.replace(/^https?:\/\//, "webcal://");

  const copyText = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} kopiert`); }
    catch { toast.error("Kunne ikke kopiere"); }
  };

  const rotateToken = async () => {
    if (!window.confirm("Generere ny hemmelig lenke? Alle som har abonnert på gammel lenke mister tilgang.")) return;
    try {
      await api.rotateCalendarFeedToken();
      toast.success("Ny lenke generert");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke rotere");
    }
  };

  const connectIcloud = async () => {
    const aid = icloudAppleId.trim();
    const pwd = icloudPassword.trim();
    if (!aid || !pwd) { toast.error("Fyll ut Apple ID og app-spesifikt passord"); return; }
    setIcloudBusy(true);
    try {
      const res = await api.connectIcloud(aid, pwd);
      toast.success(`Tilkoblet ${res.data?.calendar_name || "iCloud"} · ${res.data?.initial_sync?.pulled || 0} avtaler synket`);
      setIcloudAppleId(""); setIcloudPassword("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Tilkobling feilet");
    } finally { setIcloudBusy(false); }
  };

  const syncIcloudNow = async () => {
    setIcloudBusy(true);
    try {
      const res = await api.syncIcloudNow();
      toast.success(`Synket · ${res.data?.pulled || 0} hentet, ${res.data?.pushed || 0} sendt`);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Synk feilet");
    } finally { setIcloudBusy(false); }
  };

  const disconnectIcloud = async () => {
    if (!window.confirm("Koble fra iCloud? Synk stopper, men allerede importerte avtaler beholdes.")) return;
    setIcloudBusy(true);
    try {
      await api.disconnectIcloud();
      setIcloud({ connected: false });
      toast.success("iCloud frakoblet");
    } catch { toast.error("Kunne ikke koble fra"); }
    finally { setIcloudBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="calendar-sources-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Kalender-kilder</DialogTitle>
          <DialogDescription>
            Legg til flere kilder (skole, jobb, familie) som auto-synkroniseres daglig. Del også husstandens egen kalender med familien.
          </DialogDescription>
        </DialogHeader>

        {/* ─── Two-way sync integrations (Home Edition only) ─── */}
        {isHousehold && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2 border-b border-stone-200 pb-2">
              <ArrowDownUp className="h-4 w-4 text-stone-600" />
              <h2 className="text-sm font-bold text-stone-800 uppercase tracking-wider">Toveis-sync med ekstern kalender</h2>
            </div>

            {/* ─── Apple iCloud two-way sync ─── */}
            <section className="rounded-xl border border-stone-300 bg-white p-4" data-testid="icloud-section">
              <h3 className="text-sm font-semibold text-stone-800 mb-1 flex items-center gap-2">
                <Apple className="h-4 w-4 text-stone-800" /> Apple iCloud — ekte toveis-sync
              </h3>
              <p className="text-xs text-stone-600 mb-3">
                Endringer i Apple Calendar på iPhone/Mac dukker opp i PantryBuddy innen 15 min, og omvendt. Krever et <b>app-spesifikt passord</b> fra Apple (ikke ditt vanlige Apple-passord).
              </p>

              {!icloud?.connected && (
                <div className="space-y-2">
                  <details className="text-xs text-stone-700 mb-2 bg-stone-50 rounded-md p-2">
                    <summary className="cursor-pointer font-medium text-stone-800">📘 Slik lager du app-spesifikt passord</summary>
                    <ol className="list-decimal list-inside mt-2 space-y-1 pl-2">
                      <li>Gå til <a href="https://appleid.apple.com/account/manage" target="_blank" rel="noreferrer" className="text-sky-700 underline">appleid.apple.com</a></li>
                      <li>Logg inn → Sign-In and Security → App-Specific Passwords</li>
                      <li>Trykk "Generate an app-specific password" → gi navnet "PantryBuddy"</li>
                      <li>Kopier passordet som vises (format: xxxx-xxxx-xxxx-xxxx) — vises bare én gang!</li>
                    </ol>
                  </details>
                  <input
                    type="email"
                    placeholder="Apple ID (e-post)"
                    value={icloudAppleId}
                    onChange={(e) => setIcloudAppleId(e.target.value)}
                    className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-600 focus:outline-none"
                    data-testid="icloud-apple-id"
                    autoComplete="username"
                  />
                  <input
                    type="password"
                    placeholder="App-spesifikt passord (xxxx-xxxx-xxxx-xxxx)"
                    value={icloudPassword}
                    onChange={(e) => setIcloudPassword(e.target.value)}
                    className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-600 focus:outline-none font-mono"
                    data-testid="icloud-password"
                    autoComplete="new-password"
                  />
                  <button
                    onClick={connectIcloud}
                    disabled={icloudBusy || !icloudAppleId.trim() || !icloudPassword.trim()}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-stone-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-stone-900 disabled:opacity-50"
                    data-testid="icloud-connect-btn"
                  >
                    {icloudBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Apple className="h-4 w-4" />}
                    {icloudBusy ? "Kobler til…" : "Koble til iCloud"}
                  </button>
                </div>
              )}

              {icloud?.connected && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                      <div>
                        <p className="text-sm font-medium text-stone-900">{icloud.apple_id}</p>
                        <p className="text-[11px] text-stone-500">{icloud.calendar_name} · Sist synk: {icloud.last_sync_at ? relTime(icloud.last_sync_at) : "aldri"}</p>
                      </div>
                    </div>
                    <div className="text-right text-[11px] text-stone-600">
                      <p>{icloud.last_pulled ?? 0} hentet</p>
                      <p>{icloud.last_pushed ?? 0} sendt</p>
                    </div>
                  </div>
                  {icloud.last_error && (
                    <div className="rounded-md bg-rose-50 border border-rose-200 p-2 text-[11px] text-rose-800 flex items-start gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> {icloud.last_error}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={syncIcloudNow}
                      disabled={icloudBusy}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-2 text-xs font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
                      data-testid="icloud-sync-btn"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${icloudBusy ? "animate-spin" : ""}`} /> Synk nå
                    </button>
                    <button
                      onClick={disconnectIcloud}
                      disabled={icloudBusy}
                      className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      data-testid="icloud-disconnect-btn"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Koble fra
                    </button>
                  </div>
                  <p className="text-[11px] text-stone-400 text-center">Auto-synk kjører hvert 15. min</p>
                </div>
              )}
            </section>

            {/* Google Calendar */}
            <OAuthIntegrationCard
              label="Google Calendar"
              icon={Mail}
              testid="google-section"
              accentClass={{ border: "border-sky-200", bg: "bg-sky-50/60", icon: "text-sky-700", btn: "bg-sky-600 hover:bg-sky-700" }}
              status={googleStatus}
              getAuthorizeUrl={api.getGoogleAuthorizeUrl}
              syncNow={api.syncGoogleCalNow}
              disconnect={api.disconnectGoogleCal}
              onChanged={load}
            />

            {/* Microsoft Outlook */}
            <OAuthIntegrationCard
              label="Microsoft Outlook"
              icon={LayoutGrid}
              testid="outlook-section"
              accentClass={{ border: "border-blue-200", bg: "bg-blue-50/60", icon: "text-blue-700", btn: "bg-blue-700 hover:bg-blue-800" }}
              status={outlookStatus}
              getAuthorizeUrl={api.getOutlookAuthorizeUrl}
              syncNow={api.syncOutlookNow}
              disconnect={api.disconnectOutlook}
              onChanged={load}
            />
          </div>
        )}

        {/* ─── Add new subscription ─── */}
        <section className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 mt-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-2 flex items-center gap-2">
            <Plus className="h-4 w-4 text-amber-700" /> Legg til kalender-abonnement
          </h3>
          <p className="text-xs text-stone-600 mb-3">Fyller automatisk inn nye avtaler én gang per døgn.</p>
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Navn (f.eks. Skolen, Fotball, Jobb)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              data-testid="sub-name-input"
            />
            <div className="flex gap-2">
              <input
                type="url"
                placeholder="https://... eller webcal://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="sub-url-input"
              />
              <button onClick={pasteUrl} className="inline-flex items-center gap-1 rounded-md border border-stone-300 bg-white px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50" data-testid="sub-paste-btn">
                <ClipboardPaste className="h-3.5 w-3.5" /> Lim inn
              </button>
              <button
                onClick={addSub}
                disabled={adding || !name.trim() || !url.trim()}
                className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                data-testid="sub-add-btn"
              >
                <Plus className="h-4 w-4" /> {adding ? "Legger til…" : "Legg til"}
              </button>
            </div>
          </div>
        </section>

        {/* ─── Existing subscriptions ─── */}
        <section className="mt-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-2 flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-amber-700" /> Aktive abonnementer ({subs.length})
          </h3>
          {loading && <p className="text-sm text-stone-500">Laster…</p>}
          {!loading && subs.length === 0 && (
            <div className="rounded-md border border-dashed border-stone-300 p-4 text-center text-sm text-stone-500" data-testid="sub-empty-state">
              Ingen abonnementer ennå. Legg til en kilde ovenfor.
            </div>
          )}
          <ul className="space-y-2">
            {subs.map((s) => (
              <li key={s.id} className="border border-stone-200 rounded-lg p-3 bg-white" data-testid={`sub-${s.id}`}>
                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium text-stone-900 truncate">{s.name}</span>
                      <span className="text-[11px] text-stone-400">{s.last_error ? (
                        <span className="inline-flex items-center gap-0.5 text-rose-600"><AlertTriangle className="h-3 w-3" /> feil</span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 text-emerald-600"><CheckCircle2 className="h-3 w-3" /> ok</span>
                      )}</span>
                    </div>
                    <div className="text-xs text-stone-500 truncate" title={s.url}>{s.url}</div>
                    <div className="text-[11px] text-stone-400 mt-1">
                      Sist synk: {relTime(s.last_sync_at)} · {s.last_imported ?? 0} importert · {s.last_skipped ?? 0} hoppet over
                    </div>
                    {s.last_error && <p className="text-[11px] text-rose-600 mt-1">{s.last_error}</p>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => syncSub(s.id)}
                      disabled={busyId === s.id}
                      className="p-1.5 rounded-md hover:bg-amber-100 text-stone-500 hover:text-amber-700 disabled:opacity-50"
                      title="Synkroniser nå"
                      data-testid={`sub-sync-${s.id}`}
                    >
                      <RefreshCw className={`h-4 w-4 ${busyId === s.id ? "animate-spin" : ""}`} />
                    </button>
                    <button
                      onClick={() => deleteSub(s.id, s.name)}
                      disabled={busyId === s.id}
                      className="p-1.5 rounded-md hover:bg-rose-50 text-stone-400 hover:text-rose-600 disabled:opacity-50"
                      title="Fjern abonnement"
                      data-testid={`sub-delete-${s.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ─── Share household feed ─── */}
        {feed && (
          <section className="mt-6 rounded-xl border border-sky-200 bg-sky-50/60 p-4">
            <h3 className="text-sm font-semibold text-stone-800 mb-1 flex items-center gap-2">
              <Share2 className="h-4 w-4 text-sky-700" /> Del husstandens kalender
            </h3>
            <p className="text-xs text-stone-600 mb-3">
              Familiemedlemmer kan abonnere på denne URL-en fra telefonen sin (Google Calendar, Apple Calendar, Outlook).
              Endringer i appen vises automatisk i kalenderen deres.
            </p>
            <div className="rounded-md bg-amber-50 border border-amber-100 p-2.5 text-[11px] text-amber-900/90 mb-3">
              <p className="font-semibold mb-1">Viktig om toveis-sync</p>
              <p>Abonnement er <b>envei</b> (PantryBuddy → telefon) — endringer du gjør i Apple/Google Calendar kommer ikke tilbake hit. For å legge til eller redigere avtaler fra telefonen, bruk PantryBuddy-appen direkte. Du kan også trykke «Legg til i telefonens kalender» på en enkelt avtale for å få den i begge kalendere med ett trykk.</p>
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-stone-500">For Apple/iOS (webcal://)</label>
                <div className="flex gap-2 mt-1">
                  <input readOnly value={feedWebcalUrl} className="flex-1 rounded-md border border-stone-300 bg-white px-3 py-2 text-xs font-mono" data-testid="feed-webcal-url" />
                  <button onClick={() => copyText(feedWebcalUrl, "webcal-URL")} className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white px-3 py-2 text-xs font-medium text-sky-800 hover:bg-sky-50" data-testid="feed-copy-webcal-btn">
                    <Copy className="h-3 w-3" /> Kopier
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-stone-500">For Google/Android/nett (https://)</label>
                <div className="flex gap-2 mt-1">
                  <input readOnly value={feedHttpsUrl} className="flex-1 rounded-md border border-stone-300 bg-white px-3 py-2 text-xs font-mono" data-testid="feed-https-url" />
                  <button onClick={() => copyText(feedHttpsUrl, "URL")} className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white px-3 py-2 text-xs font-medium text-sky-800 hover:bg-sky-50" data-testid="feed-copy-https-btn">
                    <Copy className="h-3 w-3" /> Kopier
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-sky-200/70">
                <a
                  href={feedHttpsUrl}
                  download={`husstand-${feed.boat_id.slice(0,8)}.ics`}
                  className="inline-flex items-center gap-1 text-xs text-sky-800 font-medium hover:underline"
                  data-testid="feed-download-btn"
                >
                  <Download className="h-3.5 w-3.5" /> Last ned som .ics-fil
                </a>
                <button
                  onClick={rotateToken}
                  className="inline-flex items-center gap-1 text-xs text-rose-600 font-medium hover:underline"
                  data-testid="feed-rotate-btn"
                >
                  <RefreshCcw className="h-3.5 w-3.5" /> Lag ny hemmelig lenke
                </button>
              </div>
            </div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}
